import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import GoalSelectionCard from './components/GoalSelectionCard';
import GoalConfigurationForm from './components/GoalConfigurationForm';
import FeasibilityIndexCard from './components/FeasibilityIndexCard';
import GapAnalysisSection from './components/GapAnalysisSection';
import QuarterlyMilestonesSection from './components/QuarterlyMilestonesSection';
import RiskAlertsSection from './components/RiskAlertsSection';

const GoalIntelligence = () => {
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [formData, setFormData] = useState({
    targetRevenue: '',
    timeline: '',
    profitMargin: '',
    growthPercentage: '',
    targetMarkets: '',
    fundingAmount: '',
    useOfFunds: '',
    teamSize: '',
    infrastructure: ''
  });
  const [milestones, setMilestones] = useState([]);

  const goals = [
    {
      id: 'revenue',
      title: 'Increase Revenue',
      description: 'Set ambitious revenue targets and track progress with AI-powered projections and actionable insights.',
      icon: 'TrendingUp'
    },
    {
      id: 'profit',
      title: 'Improve Profit',
      description: 'Optimize profit margins through cost reduction strategies and revenue optimization techniques.',
      icon: 'DollarSign'
    },
    {
      id: 'expansion',
      title: 'Expand Market',
      description: 'Enter new markets and demographics with data-driven expansion strategies and risk assessment.',
      icon: 'Globe'
    },
    {
      id: 'funding',
      title: 'Raise Funding',
      description: 'Prepare for fundraising with investor-ready metrics, projections, and strategic use of capital planning.',
      icon: 'Wallet'
    },
    {
      id: 'scaling',
      title: 'Scale Operations',
      description: 'Grow your team and infrastructure efficiently with scalable systems and operational excellence frameworks.',
      icon: 'Rocket'
    }
  ];

  const mockAnalysisData = {
    revenue: {
      feasibilityScore: 78,
      confidence: 'high',
      currentValue: 450000,
      targetValue: 1000000,
      metric: 'Revenue',
      unit: '$',
      milestones: [
        {
          id: 1,
          quarter: 1,
          year: 2026,
          timeframe: 'Feb - Apr 2026',
          title: 'Optimize Top Revenue Channels',
          description: 'Focus on your highest-performing channels and increase conversion rates by 15%',
          metrics: ['₹625K Revenue', '15% Conversion Increase'],
          completed: false
        },
        {
          id: 2,
          quarter: 2,
          year: 2026,
          timeframe: 'May - Jul 2026',
          title: 'Launch New Product Line',
          description: 'Introduce complementary products to existing customer base',
          metrics: ['₹750K Revenue', '2 New Products'],
          completed: false
        },
        {
          id: 3,
          quarter: 3,
          year: 2026,
          timeframe: 'Aug - Oct 2026',
          title: 'Expand Customer Acquisition',
          description: 'Scale marketing efforts and enter adjacent market segments',
          metrics: ['₹875K Revenue', '30% Customer Growth'],
          completed: false
        },
        {
          id: 4,
          quarter: 4,
          year: 2026,
          timeframe: 'Nov 2026 - Jan 2027',
          title: 'Achieve Revenue Target',
          description: 'Reach ₹1M annual revenue through sustained growth and optimization',
          metrics: ['₹1M Revenue', '100% Goal Achievement'],
          completed: false
        }
      ],
      risks: [
        {
          id: 1,
          severity: 'medium',
          title: 'Market Saturation Risk',
          description: 'Current market shows signs of saturation with 12% YoY growth decline in your segment.',
          mitigation: 'Diversify into adjacent markets and develop unique value propositions. Consider geographic expansion or new customer segments.'
        },
        {
          id: 2,
          severity: 'low',
          title: 'Seasonal Revenue Fluctuation',
          description: 'Historical data shows 25% revenue variance between peak and low seasons.',
          mitigation: 'Build cash reserves during peak seasons and develop counter-seasonal product offerings to smooth revenue throughout the year.'
        }
      ]
    },
    profit: {
      feasibilityScore: 72,
      confidence: 'medium',
      currentValue: 15,
      targetValue: 30,
      metric: 'Profit Margin',
      unit: '',
      milestones: [
        {
          id: 1,
          quarter: 1,
          year: 2026,
          timeframe: 'Feb - Apr 2026',
          title: 'Cost Structure Analysis',
          description: 'Conduct comprehensive cost audit and identify optimization opportunities',
          metrics: ['18% Margin', '10% Cost Reduction'],
          completed: false
        },
        {
          id: 2,
          quarter: 2,
          year: 2026,
          timeframe: 'May - Jul 2026',
          title: 'Operational Efficiency',
          description: 'Implement automation and process improvements to reduce operational costs',
          metrics: ['22% Margin', '15% Efficiency Gain'],
          completed: false
        },
        {
          id: 3,
          quarter: 3,
          year: 2026,
          timeframe: 'Aug - Oct 2026',
          title: 'Premium Product Mix',
          description: 'Shift product mix toward higher-margin offerings',
          metrics: ['26% Margin', '20% Premium Sales'],
          completed: false
        },
        {
          id: 4,
          quarter: 4,
          year: 2026,
          timeframe: 'Nov 2026 - Jan 2027',
          title: 'Achieve Profit Target',
          description: 'Reach 30% profit margin through sustained optimization',
          metrics: ['30% Margin', '100% Goal Achievement'],
          completed: false
        }
      ],
      risks: [
        {
          id: 1,
          severity: 'high',
          title: 'Rising Input Costs',
          description: 'Supplier costs have increased 18% YoY, putting pressure on margins.',
          mitigation: 'Negotiate long-term contracts with suppliers, explore alternative vendors, and consider strategic price adjustments for customers.'
        },
        {
          id: 2,
          severity: 'medium',
          title: 'Competitive Pricing Pressure',
          description: 'Competitors are engaging in aggressive pricing strategies in your market.',
          mitigation: 'Focus on value differentiation rather than price competition. Enhance customer experience and build brand loyalty.'
        }
      ]
    },
    expansion: {
      feasibilityScore: 65,
      confidence: 'medium',
      currentValue: 0,
      targetValue: 50,
      metric: 'Market Growth',
      unit: '',
      milestones: [
        {
          id: 1,
          quarter: 1,
          year: 2026,
          timeframe: 'Feb - Apr 2026',
          title: 'Market Research & Validation',
          description: 'Conduct comprehensive market research in target regions',
          metrics: ['3 Markets Analyzed', 'Customer Validation'],
          completed: false
        },
        {
          id: 2,
          quarter: 2,
          year: 2026,
          timeframe: 'May - Jul 2026',
          title: 'Pilot Launch',
          description: 'Launch pilot programs in selected markets with localized offerings',
          metrics: ['2 Pilot Markets', '15% Growth'],
          completed: false
        },
        {
          id: 3,
          quarter: 3,
          year: 2026,
          timeframe: 'Aug - Oct 2026',
          title: 'Scale Successful Markets',
          description: 'Scale operations in validated markets and optimize go-to-market strategy',
          metrics: ['4 Active Markets', '35% Growth'],
          completed: false
        },
        {
          id: 4,
          quarter: 4,
          year: 2026,
          timeframe: 'Nov 2026 - Jan 2027',
          title: 'Full Market Expansion',
          description: 'Achieve 50% market growth through sustained expansion efforts',
          metrics: ['6 Active Markets', '50% Growth'],
          completed: false
        }
      ],
      risks: [
        {
          id: 1,
          severity: 'high',
          title: 'Cultural & Regulatory Barriers',
          description: 'New markets have different regulatory requirements and cultural preferences.',
          mitigation: 'Partner with local experts, invest in market-specific research, and ensure compliance with all regional regulations before launch.'
        },
        {
          id: 2,
          severity: 'medium',
          title: 'Resource Constraints',
          description: 'Expansion requires significant capital and human resources that may strain current operations.',
          mitigation: 'Phase expansion gradually, secure additional funding if needed, and build strategic partnerships to share expansion costs and risks.'
        }
      ]
    },
    funding: {
      feasibilityScore: 70,
      confidence: 'medium',
      currentValue: 0,
      targetValue: 2000000,
      metric: 'Funding',
      unit: '$',
      milestones: [
        {
          id: 1,
          quarter: 1,
          year: 2026,
          timeframe: 'Feb - Apr 2026',
          title: 'Investor-Ready Materials',
          description: 'Prepare comprehensive pitch deck, financial models, and business plan',
          metrics: ['Pitch Deck', 'Financial Model'],
          completed: false
        },
        {
          id: 2,
          quarter: 2,
          year: 2026,
          timeframe: 'May - Jul 2026',
          title: 'Investor Outreach',
          description: 'Connect with angel investors, VCs, and strategic partners',
          metrics: ['20 Investor Meetings', 'Term Sheet'],
          completed: false
        },
        {
          id: 3,
          quarter: 3,
          year: 2026,
          timeframe: 'Aug - Oct 2026',
          title: 'Due Diligence',
          description: 'Complete investor due diligence and negotiate terms',
          metrics: ['Due Diligence', 'Final Terms'],
          completed: false
        },
        {
          id: 4,
          quarter: 4,
          year: 2026,
          timeframe: 'Nov 2026 - Jan 2027',
          title: 'Close Funding Round',
          description: 'Finalize agreements and secure $2M in funding',
          metrics: ['$2M Raised', '100% Goal Achievement'],
          completed: false
        }
      ],
      risks: [
        {
          id: 1,
          severity: 'high',
          title: 'Market Conditions',
          description: 'Current economic uncertainty may impact investor appetite and valuations.',
          mitigation: 'Build strong fundamentals, demonstrate clear path to profitability, and consider alternative funding sources like strategic partnerships or revenue-based financing.'
        },
        {
          id: 2,
          severity: 'medium',
          title: 'Valuation Expectations',
          description: 'Gap between founder expectations and investor valuations could delay funding.',
          mitigation: 'Conduct thorough market comparables analysis, be flexible on terms, and focus on finding the right strategic partner rather than just the highest valuation.'
        }
      ]
    },
    scaling: {
      feasibilityScore: 68,
      confidence: 'medium',
      currentValue: 15,
      targetValue: 50,
      metric: 'Team Size',
      unit: '',
      milestones: [
        {
          id: 1,
          quarter: 1,
          year: 2026,
          timeframe: 'Feb - Apr 2026',
          title: 'Infrastructure Setup',
          description: 'Implement scalable systems, tools, and processes',
          metrics: ['25 Team Members', 'Core Systems'],
          completed: false
        },
        {
          id: 2,
          quarter: 2,
          year: 2026,
          timeframe: 'May - Jul 2026',
          title: 'Key Hires',
          description: 'Recruit senior leadership and specialized talent',
          metrics: ['35 Team Members', '5 Senior Hires'],
          completed: false
        },
        {
          id: 3,
          quarter: 3,
          year: 2026,
          timeframe: 'Aug - Oct 2026',
          title: 'Team Expansion',
          description: 'Scale teams across departments with structured onboarding',
          metrics: ['42 Team Members', 'Training Program'],
          completed: false
        },
        {
          id: 4,
          quarter: 4,
          year: 2026,
          timeframe: 'Nov 2026 - Jan 2027',
          title: 'Achieve Scale Target',
          description: 'Reach 50-person team with optimized operations',
          metrics: ['50 Team Members', '100% Goal Achievement'],
          completed: false
        }
      ],
      risks: [
        {
          id: 1,
          severity: 'high',
          title: 'Talent Acquisition Challenges',
          description: 'Competitive market for skilled talent may slow hiring and increase costs.',
          mitigation: 'Build strong employer brand, offer competitive compensation and equity, create clear career paths, and consider remote hiring to expand talent pool.'
        },
        {
          id: 2,
          severity: 'medium',
          title: 'Culture Dilution',
          description: 'Rapid growth can dilute company culture and reduce team cohesion.',
          mitigation: 'Document core values, implement structured onboarding, maintain regular team communication, and preserve cultural rituals as you scale.'
        }
      ]
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAnalyzeGoal = () => {
    if (!selectedGoal) return;
    
    const analysisData = mockAnalysisData?.[selectedGoal];
    setMilestones(analysisData?.milestones);
    setShowAnalysis(true);
  };

  const handleToggleMilestone = (milestoneId) => {
    setMilestones(prev =>
      prev?.map(m =>
        m?.id === milestoneId ? { ...m, completed: !m?.completed } : m
      )
    );
  };

  const handleReset = () => {
    setSelectedGoal(null);
    setShowAnalysis(false);
    setFormData({
      targetRevenue: '',
      timeline: '',
      profitMargin: '',
      growthPercentage: '',
      targetMarkets: '',
      fundingAmount: '',
      useOfFunds: '',
      teamSize: '',
      infrastructure: ''
    });
    setMilestones([]);
  };

  const currentAnalysis = selectedGoal ? mockAnalysisData?.[selectedGoal] : null;

  return (
    <div className="min-h-screen bg-background">
      <AuthHeader isAuthenticated userName="Business Owner" userSubscription="free" />
      <Sidebar userSubscription="free" />
      <main className="lg:ml-60 pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                  Goal Intelligence
                </h1>
                <p className="text-sm md:text-base text-muted-foreground font-body">
                  Set strategic objectives and track progress with AI-powered feasibility analysis
                </p>
              </div>
              {showAnalysis && (
                <Button
                  variant="outline"
                  size="default"
                  onClick={handleReset}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Reset Goal
                </Button>
              )}
            </div>
          </div>

          {!showAnalysis ? (
            <>
              <div className="mb-6 md:mb-8">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4">
                  Select Your Primary Goal
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {goals?.map((goal) => (
                    <GoalSelectionCard
                      key={goal?.id}
                      goal={goal}
                      isSelected={selectedGoal === goal?.id}
                      onSelect={setSelectedGoal}
                    />
                  ))}
                </div>
              </div>

              {selectedGoal && (
                <div className="bg-card rounded-xl border border-border p-6 md:p-8 elevation-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Icon name="Settings" size={20} color="var(--color-primary)" />
                    </div>
                    <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                      Configure Your Goal
                    </h2>
                  </div>

                  <GoalConfigurationForm
                    selectedGoal={selectedGoal}
                    formData={formData}
                    onChange={handleFormChange}
                  />

                  <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="default"
                      size="lg"
                      onClick={handleAnalyzeGoal}
                      iconName="Sparkles"
                      iconPosition="left"
                      fullWidth
                    >
                      Analyze Goal Feasibility
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => setSelectedGoal(null)}
                      fullWidth
                    >
                      Change Goal
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-6 md:space-y-8">
              <FeasibilityIndexCard
                score={currentAnalysis?.feasibilityScore}
                confidence={currentAnalysis?.confidence}
              />

              <GapAnalysisSection
                currentValue={currentAnalysis?.currentValue}
                targetValue={currentAnalysis?.targetValue}
                metric={currentAnalysis?.metric}
                unit={currentAnalysis?.unit}
              />

              <QuarterlyMilestonesSection
                milestones={milestones}
                onToggleMilestone={handleToggleMilestone}
              />

              <RiskAlertsSection risks={currentAnalysis?.risks} />

              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-primary/20 p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="FileText" size={24} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                      Generate Comprehensive Goal Report
                    </h3>
                    <p className="text-sm text-muted-foreground font-body">
                      Download a detailed PDF report with feasibility analysis, milestones, risk assessment, and actionable recommendations
                    </p>
                  </div>
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Download"
                    iconPosition="right"
                    className="w-full md:w-auto"
                  >
                    Download Report
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default GoalIntelligence;